import { Box, Dialog as MDialog, DialogContent, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import FHButton from '../FHButton';
import FProgressBar from '../FProgressBar';
import { FORTA_GRAY4, FORTA_BLUE, FORTA_WHITE } from '../../../utils/colors';
import { getThemeColor } from '../../../utils/common';
import { ProgressBarThemes, Themes } from '../../../constants';
import CloseIcon from '../../svgs/CloseIcon';

import type { DialogProps as MDialogProps, LinearProgressProps } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import { isValidElement } from 'react';

interface ConfirmDialogProps extends Omit<MDialogProps, 'onClose'> {
  title?: string;
  content?: string;
  header?: string | ReactElement;
  submittext?: string;
  canceltext?: string;
  hideSubmitButton?: boolean;
  hideCancelButton?: boolean;
  hideCloseIcon?: boolean;
  showProgressBar?: boolean;
  progressBarTheme?: LinearProgressProps['color'];
  submitLoading?: boolean;
  colorTheme?: Themes;
  cancelStartIcon?: ReactNode;
  submitStartIcon?: ReactNode;
  contentRenderer?: () => ReactNode;
  titleRenderer?: () => ReactNode;
  disableSubmitButton?: boolean;
  disableCancelButton?: boolean;
  submitButtonStyles?: any;
  dialogStyles?: any;
  headerStyles?: any;
  titleStyles?: any;
  footerContainerStyles?: any;
  submitBtnTestId?: string;
  cancelBtnTestId?: string;
  closeIconDataTestId?: string;
  dialogHeight?: number | string;
  onClose?: MDialogProps['onClose'];
  onSubmit?: () => void;
}

const StyledDialog = styled(MDialog, {
  shouldForwardProp: (prop) =>
    prop !== 'colorTheme' && prop !== 'dialogStyles' && prop !== 'dialogHeight',
})<ConfirmDialogProps>(({ dialogStyles, sx, dialogHeight = 300 }: any) => ({
  '& .MuiDialog-paper': {
    width: 650,
    height: dialogHeight,
    maxWidth: 650,
    maxHeight: dialogHeight,
    backgroundColor: FORTA_WHITE,
    backgroundSize: '100%',
    boxShadow: `8px 8px 8px 0px rgba(74, 74, 74, 0.25)`,
    borderRadius: '8px',
    margin: 0,
    padding: '24px',
    border: `2px solid ${FORTA_BLUE.light3}`,
    ...dialogStyles,
  },
  '& .MuiDialogContent-root': {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  ...sx,
}));

function FHConfirmDialog(props: ConfirmDialogProps) {
  const {
    open,
    title = '',
    content,
    submittext,
    canceltext,
    submitLoading = false,
    hideCancelButton = false,
    hideSubmitButton = false,
    hideCloseIcon = false,
    showProgressBar = false,
    progressBarTheme = ProgressBarThemes.Primary,
    colorTheme = Themes.Primary,
    cancelStartIcon,
    submitStartIcon,
    onSubmit,
    onClose,
    contentRenderer,
    titleRenderer,
    disableSubmitButton = false,
    disableCancelButton = false,
    submitButtonStyles = {},
    footerContainerStyles = {},
    dialogStyles = {},
    header,
    submitBtnTestId,
    cancelBtnTestId,
    closeIconDataTestId,
    titleStyles = {},
    ...rest
  } = props;

  const handleCloseClick = () => onClose?.({}, 'escapeKeyDown');

  return (
    <StyledDialog {...rest} open={open} dialogStyles={dialogStyles}>
      {showProgressBar && (
        <FProgressBar
          sx={{
            position: 'absolute',
            width: '100%',
          }}
          color={progressBarTheme}
        />
      )}
      <DialogContent>
        {!hideCloseIcon && (
          <IconButton
            data-testid={closeIconDataTestId}
            sx={{ p: 0, position: 'absolute', top: 32, right: 24 }}
            disableRipple
            onClick={handleCloseClick}
          >
            <CloseIcon color={getThemeColor(colorTheme).primary} />
          </IconButton>
        )}
        {title && !titleRenderer && (
          <Typography variant="h4" fontWeight={500} align="left" sx={{ ...titleStyles }}>
            {title}
          </Typography>
        )}
        {titleRenderer && titleRenderer()}

        {header && !isValidElement(header) ? (
          <Typography fontSize={'14px'} lineHeight={'21px'} fontWeight={400} align="left" my={3}>
            {header}
          </Typography>
        ) : (
          header
        )}

        {content && (
          <Typography
            variant="h4"
            align="center"
            color={FORTA_GRAY4}
            flex={'1'}
            display="flex"
            alignItems={'center'}
            justifyContent={'center'}
            width={'435px'}
            alignSelf={'center'}
          >
            {content}
          </Typography>
        )}

        {contentRenderer && contentRenderer()}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1, ...footerContainerStyles }}>
          {!hideCancelButton && (
            <FHButton
              data-testid={cancelBtnTestId}
              colorTheme={colorTheme}
              onClick={handleCloseClick}
              lightBackground
              disabled={disableCancelButton}
              startIcon={cancelStartIcon}
              sx={{
                backgroundColor: FORTA_WHITE,
                border: `1px solid ${getThemeColor(colorTheme).light2}`,
                color: FORTA_GRAY4,
                mr: 3,
                '&:hover': {
                  backgroundColor: FORTA_WHITE,
                  border: `1px solid ${getThemeColor(colorTheme).light2}`,
                  color: FORTA_GRAY4,
                },
              }}
            >
              {canceltext}
            </FHButton>
          )}
          {!hideSubmitButton && (
            <FHButton
              colorTheme={colorTheme}
              loading={submitLoading}
              startIcon={submitStartIcon}
              onClick={onSubmit}
              disabled={disableSubmitButton}
              sx={{ ...submitButtonStyles }}
              data-testid={submitBtnTestId}
            >
              {submittext}
            </FHButton>
          )}
        </Box>
      </DialogContent>
    </StyledDialog>
  );
}

export default FHConfirmDialog;
